import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  Building2,
  HelpCircle,
  Compass,
  Info,
  Loader2,
  Search,
  ShieldCheck,
  UserPlus,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  dbService,
  type ExpertGuidanceHelpType,
  type ExpertGuidanceRequestPayload,
} from '../lib/supabase';
import { validateEmail } from '../utils/validations';

type HelpType = ExpertGuidanceHelpType;

const helpOptions: Array<{
  id: HelpType;
  label: string;
  Icon: React.ComponentType<{ className?: string; size?: number; strokeWidth?: number }>;
}> = [
  { id: 'cdmo_selection', label: 'CDMO selection', Icon: Building2 },
  { id: 'supplier_sourcing', label: 'Supplier sourcing', Icon: Search },
  { id: 'project_guidance', label: 'Project guidance', Icon: Compass },
  { id: 'execution_support', label: 'Execution support', Icon: BarChart3 },
  { id: 'other', label: 'Other', Icon: HelpCircle },
];

const timelineOptions = [
  '',
  'ASAP',
  'Within 2–4 weeks',
  '1–3 months',
  'Just exploring',
  'Not sure yet',
];

export interface ExpertGuidanceFeatureProps {
  sponsorName?: string;
  sponsorEmail?: string;
  sponsorCompany?: string;
}

export default function ExpertGuidanceFeature({
  sponsorName = '',
  sponsorEmail = '',
  sponsorCompany = '',
}: ExpertGuidanceFeatureProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <ExpertGuidanceBanner onRequest={() => setIsOpen(true)} />

      <ExpertGuidanceModal
        isOpen={isOpen}
        sponsorName={sponsorName}
        sponsorEmail={sponsorEmail}
        sponsorCompany={sponsorCompany}
        onClose={() => setIsOpen(false)}
        onSuccess={() => setIsOpen(false)}
      />
    </>
  );
}

export function ExpertGuidanceBanner({ onRequest }: { onRequest: () => void }) {
  return (
    <section
      className="mb-6 grid grid-cols-1 items-center gap-4 rounded-2xl border border-electric-blue/30 bg-white/95 p-5 shadow-lg backdrop-blur-sm md:grid-cols-[auto_1fr_auto] md:gap-5 md:p-6"
      aria-label="Optional expert guidance"
    >
      <div
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-electric-blue/10 text-electric-blue md:h-16 md:w-16"
        aria-hidden="true"
      >
        <ShieldCheck className="h-8 w-8 md:h-9 md:w-9" strokeWidth={2.1} />
      </div>

      <div className="min-w-0">
        <p className="mb-1 text-sm font-semibold text-electric-blue">Optional support</p>
        <h2 className="text-lg font-bold text-charcoal md:text-xl">Need expert guidance?</h2>
        <p className="mt-1 text-sm text-slate-gray">
          Get help with CDMO selection, supplier sourcing, or project support.
        </p>
        <p className="mt-2 flex items-center gap-2 text-xs text-slate-gray md:text-sm">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-electric-blue" strokeWidth={2.1} />
          <span>
            PCN reviews requests first. No confidential details required. No obligation.
          </span>
        </p>
      </div>

      <motion.button
        type="button"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onRequest}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-primary px-5 py-3 text-sm font-semibold text-white shadow-md transition-shadow hover:shadow-lg md:w-auto md:min-w-[220px]"
      >
        <UserPlus className="h-4 w-4" strokeWidth={2.2} />
        Request Expert Guidance
      </motion.button>
    </section>
  );
}

export function ExpertGuidanceModal({
  isOpen,
  sponsorName,
  sponsorEmail,
  sponsorCompany,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  sponsorName?: string;
  sponsorEmail?: string;
  sponsorCompany?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [helpTypes, setHelpTypes] = useState<HelpType[]>([]);
  const [otherHelpText, setOtherHelpText] = useState('');
  const [projectCompanyName, setProjectCompanyName] = useState('');
  const [briefDescription, setBriefDescription] = useState('');
  const [assistanceTimeline, setAssistanceTimeline] = useState('');
  const [preferredFollowUpEmail, setPreferredFollowUpEmail] = useState(sponsorEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const showOtherField = helpTypes.includes('other');

  useEffect(() => {
    if (isOpen) {
      setPreferredFollowUpEmail(sponsorEmail);
      setSubmitError(null);
    }
  }, [isOpen, sponsorEmail]);

  const toggleHelpType = (id: HelpType) => {
    setHelpTypes((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const resetForm = () => {
    setHelpTypes([]);
    setOtherHelpText('');
    setProjectCompanyName('');
    setBriefDescription('');
    setAssistanceTimeline('');
    setPreferredFollowUpEmail(sponsorEmail);
    setSubmitError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (helpTypes.length === 0) {
      setSubmitError('Please select at least one help type.');
      return;
    }

    const contactEmail = (preferredFollowUpEmail || sponsorEmail || '').trim();
    if (!contactEmail) {
      setSubmitError('Please provide a follow-up email so we can reach you.');
      return;
    }

    const emailValidation = validateEmail(contactEmail);
    if (!emailValidation.isValid) {
      setSubmitError('Please enter a valid follow-up email address.');
      return;
    }

    if (showOtherField && !otherHelpText.trim()) {
      setSubmitError('Please describe what you need help with when Other is selected.');
      return;
    }

    setIsSubmitting(true);

    const payload: ExpertGuidanceRequestPayload = {
      sponsorName: sponsorName || undefined,
      sponsorEmail: sponsorEmail || undefined,
      sponsorCompany: sponsorCompany || undefined,
      helpTypes,
      otherHelpText: showOtherField ? otherHelpText.trim() : undefined,
      projectCompanyName: projectCompanyName.trim() || undefined,
      briefDescription: briefDescription.trim() || undefined,
      assistanceTimeline: assistanceTimeline || undefined,
      preferredFollowUpEmail: contactEmail,
      submittedFromPage: '/matchmaking',
    };

    try {
      await dbService.submitExpertGuidanceRequest(payload);
      toast.success('Your expert guidance request was submitted. Our team will review it shortly.');
      resetForm();
      onSuccess();
    } catch (err) {
      console.error('Expert guidance submit error:', err);
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Something went wrong while submitting your request. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-charcoal/50 p-4 backdrop-blur-sm sm:items-center"
          role="presentation"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pcn-expert-guidance-title"
            className="relative my-4 w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl border border-white/50 bg-white/95 p-6 shadow-2xl backdrop-blur-sm sm:my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute right-4 top-4 rounded-lg p-1.5 text-charcoal transition-colors hover:bg-light-gray"
              aria-label="Close expert guidance modal"
              onClick={onClose}
            >
              <X className="h-5 w-5" strokeWidth={2.1} />
            </button>

            <form noValidate onSubmit={handleSubmit}>
              <header className="pr-8">
                <h2 id="pcn-expert-guidance-title" className="text-xl font-bold text-charcoal sm:text-2xl">
                  Request Expert Guidance
                </h2>
                <p className="mt-2 text-sm text-slate-gray">
                  Get help with CDMO selection, supplier sourcing, or project support.
                </p>
                <p className="mt-1 text-sm text-slate-gray">
                  Submit a brief request and our team will review it before connecting you with a
                  vetted expert.
                </p>
              </header>

              <div className="mt-4 flex items-center gap-2 rounded-xl border border-electric-blue/30 bg-electric-blue/5 px-3 py-2.5 text-xs font-medium text-charcoal sm:text-sm">
                <ShieldCheck className="h-4 w-4 shrink-0 text-electric-blue" strokeWidth={2.1} />
                <span>
                  PCN reviews requests first. No confidential details required. No obligation.
                </span>
              </div>

              <div className="mt-4">
                <label className="mb-2 block text-sm font-semibold text-charcoal">
                  1. What do you need help with?
                </label>
                <div
                  className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
                  aria-label="Help type options"
                >
                  {helpOptions.map(({ id, label, Icon }) => {
                    const selected = helpTypes.includes(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`relative flex min-h-[80px] flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3 text-center text-xs font-semibold transition-all sm:text-sm ${
                          selected
                            ? 'border-electric-blue bg-electric-blue/5 text-charcoal shadow-sm ring-1 ring-electric-blue'
                            : 'border-gray-200 bg-white text-charcoal hover:border-electric-blue/50 hover:shadow-sm'
                        }`}
                        onClick={() => toggleHelpType(id)}
                        aria-pressed={selected}
                      >
                        <Icon className="h-6 w-6 text-electric-blue" strokeWidth={2.1} />
                        <span>{label}</span>
                        {selected && (
                          <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-electric-blue text-[10px] font-bold text-white">
                            ✓
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <AnimatePresence>
                {showOtherField && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 overflow-hidden"
                  >
                    <label htmlFor="pcn-eg-other" className="mb-2 block text-sm font-semibold text-charcoal">
                      If other, tell us what you need help with
                    </label>
                    <input
                      id="pcn-eg-other"
                      type="text"
                      value={otherHelpText}
                      onChange={(e) => setOtherHelpText(e.target.value)}
                      placeholder="Enter a short note..."
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-charcoal outline-none transition focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20"
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-4">
                <label htmlFor="pcn-eg-project" className="mb-2 block text-sm font-semibold text-charcoal">
                  2. Project / company name <span className="font-normal text-slate-gray">(optional)</span>
                </label>
                <input
                  id="pcn-eg-project"
                  type="text"
                  value={projectCompanyName}
                  onChange={(e) => setProjectCompanyName(e.target.value)}
                  placeholder="Enter project or company name"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-charcoal outline-none transition focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20"
                />
              </div>

              <div className="mt-4">
                <label htmlFor="pcn-eg-description" className="mb-2 block text-sm font-semibold text-charcoal">
                  3. Brief description
                </label>
                <textarea
                  id="pcn-eg-description"
                  value={briefDescription}
                  onChange={(e) => setBriefDescription(e.target.value)}
                  placeholder="Tell us a little more about what you need help with..."
                  rows={3}
                  className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-charcoal outline-none transition focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20"
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="pcn-eg-timeline" className="mb-2 block text-sm font-semibold text-charcoal">
                    4. When do you need assistance?
                  </label>
                  <select
                    id="pcn-eg-timeline"
                    value={assistanceTimeline}
                    onChange={(e) => setAssistanceTimeline(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-charcoal outline-none transition focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20"
                  >
                    {timelineOptions.map((option) => (
                      <option key={option || 'empty'} value={option}>
                        {option || 'Select timeline'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="pcn-eg-email" className="mb-2 block text-sm font-semibold text-charcoal">
                    5. Preferred follow-up email <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="pcn-eg-email"
                    type="email"
                    aria-required="true"
                    value={preferredFollowUpEmail}
                    onChange={(e) => setPreferredFollowUpEmail(e.target.value)}
                    placeholder={sponsorEmail || 'name@pharmaconnectnavigator.com'}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-charcoal outline-none transition focus:border-electric-blue focus:ring-2 focus:ring-electric-blue/20"
                  />
                </div>
              </div>

              <div className="mt-4 flex gap-3 rounded-xl border border-electric-blue/20 bg-light-gray/80 p-4">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-charcoal" strokeWidth={1.9} />
                <p className="text-xs leading-relaxed text-charcoal sm:text-sm">
                  By submitting, you confirm this request does not include confidential information.
                  PCN may review and route non-confidential details to a vetted life sciences
                  professional if there may be a fit. No paid commitment.
                </p>
              </div>

              {submitError && (
                <p
                  role="alert"
                  className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {submitError}
                </p>
              )}

              <footer className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-charcoal transition hover:bg-light-gray"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>

                <motion.button
                  type="submit"
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-primary px-6 py-2.5 text-sm font-semibold text-white shadow-md disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </motion.button>
              </footer>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
